<template>
<div>
  <div class="card card-nav-tabs">
    <div class="card-header" :class="headerGradient" v-if="options.showCardHeader">
      <div class="nav-tabs-navigation">
        <div class="nav-tabs-wrapper">
          <span class="nav-tabs-title">{{title.length > 0 ? title + ':' : ''}}</span>
          <ul class="nav nav-tabs" data-tabs="tabs">
            <li v-for="item in radioFilter" @click="onChangeFilter(item.key, item.value)" :key="item.value"  :class="{ active : filter === item.value}">
              <a href="" data-toggle="tab"> {{ $t('datatables.'+item.titleKey) }}
                <div class="ripple-container"></div>
              </a>
            </li>
          </ul>
        </div>
      </div>
      <underlined-input v-if="_options.hasSearch" v-model="searchStr" :placeholder="$t('datatables.search')" class="header-underlined-input" color="white" @updateValue="searchUpdate"/>
    </div>
    <div class="card-content table-responsive">
      <div class="table-controls">
        <dates-filter v-if="_options.hasDatesFilter" :headerGradient="headerGradient" :color="color" :canDateRange="_options.hasDateRange" startPlaceholder="00/00/0000" endPlaceholder="00/00/0000" :maxStartDate="today" :minEndDate="startDate" :startDate="startDate" :endDate="endDate" :onChange="changeDates"/>
        <div class="advanced-search" v-if="_options.hasAdvancedSearch">
          <i class="material-icons mr8">highlight_off</i>
          <outlined-button text="Advanced Search" :action="() => testDonappDialog = true" />
        </div>
      </div>
      <table class="table">
        <thead class="text-primary">
          <tr>
            <th v-for="item in tableHeaders" @click="sortBy(item.key)" :key="item.key">
              <div class="table-head-wrapper">
                <span class="clickable">{{$t('datatables.'+item.titleKey)}}</span>
                <span class="sort-controls">
                  <i class="material-icons" v-show="!(sort === item.key && sortOrder === 'desc')">arrow_drop_up</i>
                  <i class="material-icons" v-show="!(sort === item.key && sortOrder === 'asc')">arrow_drop_down</i>
                </span>
              </div>
            </th>
            <th v-if="rowActions.length > 0">
              <span>{{$t('datatables.actions')}}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.id" v-show="typeof row.show === 'undefined' ? true : row.show" :style="{ background: rowBackground(row)}">
            <TableCell v-for="field in tableHeaders" :key="field.key" :id="row[field.key] && row[field.key]._id ? row[field.key]._id : ''" :value="getValue(row, field)" :options="field.options" :type="field.type"/>
            <td class="td-actions" v-if="rowActions.length > 0">
              <button v-for="rowAction in rowActions" :key="rowAction.name" v-show="!rowAction.showAction ? true : rowAction.showAction(row)" @click="onAction(rowAction.name, row, index, rows)" type="button" rel="tooltip" class="btn btn-info btn-simple" :data-original-title="rowAction.title || ''" :title="rowAction.title || ''">
                <i :class="rowAction.dynamicIcon ? rowAction.dynamicIcon(row) : rowAction.icon" :style="{color: rowAction.color}"/>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-if="!loading && rows.length === 0" class="text-center" style="padding-top:15px">{{$t('datatables.no_results')}}</p>
      <p v-if="!loading && errorMessage && errorMessage.length >= 1" class="text-center" style="padding-top:15px">{{errorMessage}}</p>
      <div v-if="loading" style="padding-top:15px">
        <svg class="circle-loader" width="40" height="40" version="1.1" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="15" />
        </svg>
      </div>
    </div>
    <div class="footer flex">
      <donapp-button class="gradient-merchant" text="Create New" :action="onAdd" v-show="canAdd"/>
      <div class="pagination" v-if="totPages > 1">
        <button :class="{ disabled: page === 1 || loading}" @click="onChangePage(1)" :disabled="page === 1">{{$t('datatables.first')}}</button>
        <button :class="{ disabled: page === 1 || loading}" @click="onChangePage(page-1)" :disabled="page === 1">{{$t('datatables.previous')}}</button>
        <button :class="{ page: true, active: page === firstPage}" @click="onChangePage(firstPage)">{{firstPage}}</button>
        <button :class="{ page: true, active: page === secondPage, hidden: totPages < 2}" @click="onChangePage(secondPage)">{{secondPage}}</button>
        <button :class="{ page: true, active: page === thirdPage, hidden: totPages < 3}" @click="onChangePage(thirdPage)">{{thirdPage}}</button>
        <button :class="{ disabled: page === totPages || loading}" @click="onChangePage(page+1)" :disabled="page === totPages">{{$t('datatables.next')}}</button>
        <button :class="{ disabled: page === totPages || loading}" @click="onChangePage(totPages)" :disabled="page === totPages">{{$t('datatables.last')}}</button>
      </div>
    </div>
  </div>
</div>
</template>

<script>
import { mapState } from 'vuex'
import TableCell from './TableCell'
import DatePicker from 'vue-md-date-picker'
import DatesFilter from '@/components/shared/inputs/DatesFilter'
import UnderlinedInput from '@/components/shared/inputs/UnderlinedInput'
import OutlinedButton from '@/components/shared/buttons/OutlinedButton'
import DonappButton from '@/components/shared/buttons/Button'
import DonappDialog from '@/components/shared/Dialog'
import moment from 'moment'
import _ from 'lodash'

export default {
  name: 'data-table',
  components: {
    TableCell,
    DatePicker,
    DatesFilter,
    OutlinedButton,
    UnderlinedInput,
    DonappDialog,
    DonappButton
  },
  props: {
    title: {
      type: String,
      required: true
    },
    radioFilter: {
      type: Array,
      validator: function(radioElements) {
        return radioElements.length <= 5
      }
    },
    options: {
      type: Object,
      default() {
        return {
          hasDatesFilter: true,
          hasDateRange: true,
          hasSearch: true,
          hasAdvancedSearch: false,
          showCardHeader: true
        }
      }
    },
    rowBackground: {
      default: () => row => '#ffffff'
    },
    canAdd: {
      type: Boolean,
      default: false
    },
    onAdd: {
      type: Function,
      default: () => console.log('add clicked')
    },
    tableHeaders: {
      type: Array,
      required: true,
      validator: function(headers) {
        return !headers.some(header => header === null || typeof header !== 'object' || !header.key)
      }
    },
    rowActions: {
      type: Array,
      default: () => [],
      validator: function(values) {
        return !values.some(
          value => value === null || typeof value !== 'object' || (!value.icon && !value.dynamicIcon) || !value.name
        )
      }
    },
    paginationOptions: {
      type: Object,
      default() {
        return {
          endlessScroll: false,
          perPage: 20
        }
      }
    },
    onAction: {
      type: Function,
      default: (action, row, index) => console.log(JSON.stringify({ action, row, index }))
    },
    getData: {
      type: Function
    },
    getValue: {
      type: Function,
      default: (row, field) => {
        if (field.type === 'icon') {
          return field.getIcon(row[field.key])
        } else if (field.getValue) {
          return field.getValue(row, field)
        } else return _.get(row, field.key)
      }
    }
  },
  data() {
    return {
      showStartDate: false,
      showEndDate: false,
      today: moment().format('YYYY-MM-DD'),
      startDate: null,
      endDate: null,
      searchStr: '',
      sort: 'date',
      sortOrder: 'desc',
      filter: null,
      rows: [],
      page: 1,
      totPages: 0,
      loading: false,
      errorMessage: null,
      filters: {
        startDate: null,
        endDate: null,
        filters: {}
      }
    }
  },
  mounted() {
    if (this.route.params.filters) this.filters = { ...this.route.params }
    this.getDataAsync(1, this.filters)
  },
  methods: {
    changeStatus: function(id) {
      this.rows.map(row => {
        if (row._id === id) {
          row.status = !row.status
        }
      })
    },
    changeDates: function(key, value) {
      key === 'startDate' ? (this.startDate = value) : (this.endDate = value)
      this.onChangeFilter(key, value)
    },
    searchUpdate: _.debounce(function(newValue) {
      this.searchStr = newValue.toLowerCase()
      this.onChangeFilter('q', this.searchStr.trim().length > 0 ? this.searchStr.trim() : null)
    }, 300),
    sortBy: function(key) {
      this.sort = key
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      this.onChangeSorting(key, this.sortOrder)
    },
    onChangePage: function(page) {
      this.getDataAsync(page)
    },
    onChangeFilter: function(key, value) {
      if (key === 'startDate' || key === 'endDate') value ? (value = value.toString()) : (value = null)
      if (key === 'endDate') {
        /* eslint-disable */
        value
          ? (value = moment(value)
              .add(1, 'days')
              .toString())
          : (value = null)
        /* eslint-enable */
      }

      if (key === 'startDate' || key === 'endDate' || key === 'q') this.filters = { ...this.filters, [key]: value }
      else value === null ? delete this.filters.filters[key] : (this.filters.filters[key] = value)
      this.getDataAsync(1)
    },
    onChangeSorting: function(key, value) {
      this.filters = { ...this.filters, sortBy: key + ',' + value }
      this.getDataAsync()
    },
    getDataAsync: async function(page) {
      this.rows = []
      this.loading = true
      try {
        const { data: result } = await this.getData(page, this.filters)
        if (result.success) {
          this.rows = result.data.results
          this.totPages = Math.ceil(result.data.total / 20)
          this.page = result.data.page + 1
        } else {
          this.$toasted.error(this.$i18n.t('errors.something_wrong') + ': ' + result.error.message)
        }
      } catch (error) {
        this.$toasted.error(this.$i18n.t('errors.something_wrong') + ': ' + error)
      }
      this.loading = false
    },
    refresh: function() {
      this.getDataAsync(this.page)
    }
  },
  computed: {
    ...mapState({
      // arrow functions can make the code very succinct!
      headerGradient: state => {
        switch (state.dashboardType) {
          case 'user':
            return 'gradient-user'
          case 'merchant':
            return 'gradient-merchant'
          case 'npo':
            return 'gradient-npo'
          default:
            return 'gradient-donapp'
        }
      },
      color: state => {
        switch (state.dashboardType) {
          case 'user':
            return '#3a86be'
          case 'merchant':
            return '#3bb2b8'
          case 'npo':
            return '#f44ea2'
          default:
            return '#3a86be'
        }
      },
      route: state => state.route
    }),
    _options: function() {
      return {
        hasDatesFilter: true,
        hasDateRange: true,
        hasSearch: true,
        hasAdvancedSearch: false,
        showCardHeader: true,
        ...this.options
      }
    },
    firstPage: function() {
      if (this.page === 1) return this.page
      else if (this.totPages - this.page === 2) return this.totPages - 2
      else return this.page - 1
    },
    secondPage: function() {
      return this.firstPage + 1 <= this.totPages ? this.firstPage + 1 : ''
    },
    thirdPage: function() {
      return this.firstPage + 2 <= this.totPages ? this.firstPage + 2 : ''
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../../scss/config.scss';
@import '../../../scss/table.scss';
</style>



// WEBPACK FOOTER //
// src/components/shared/dataTable/DataTable.vue